import { useNuxtApp } from '#app'

type ActivePromotionCookie = ActivePromotionData & {
  expiresAt: number
}

const COOKIE_KEY = 'active-promotion'
const TTL_MINUTES = 30

/**
 * Active promotion context stored in a short-lived cookie.
 *
 * Usage:
 * - setActivePromotion(...) on banner/product click (last-touch wins)
 * - getActivePromotion() on PLP/PDP or elsewhere to attribute products
 * - clearActivePromotion() on purchase or after first qualifying attribution
 */
const useActivePromotion = () => {
  const { $cookies } = useNuxtApp() as unknown as {
    $cookies: () => {
      get: (key: string) => any
      set: (key: string, value: string, opts?: Record<string, any>) => void
      remove: (key: string, opts?: Record<string, any>) => void
    }
  }

  const getCookieStore = () => $cookies()

  const getActivePromotion = (): ActivePromotionData | null => {
    try {
      const raw = getCookieStore().get(COOKIE_KEY)
      if (!raw) return null

      const { expiresAt, ...data }: ActivePromotionCookie = typeof raw === 'string'
        ? JSON.parse(raw)
        : raw

      if (!expiresAt || expiresAt <= Date.now()) {
        clearActivePromotion()
        return null
      }

      return data
    } catch {
      clearActivePromotion()
      return null
    }
  }


  const setActivePromotion = (data: ActivePromotionData) => {
    const expiresAt = Date.now() + TTL_MINUTES * 60 * 1000
    const payload: ActivePromotionCookie = { ...data, expiresAt }
    getCookieStore().set(COOKIE_KEY, JSON.stringify(payload), {
      path: '/',
      maxAge: TTL_MINUTES * 60,
      sameSite: 'lax',
    })
  }

  const clearActivePromotion = () => {
    getCookieStore().remove(COOKIE_KEY, { path: '/' })
  }

  return {
    getActivePromotion,
    setActivePromotion,
    clearActivePromotion,
  }
}

export default useActivePromotion
